import {
  DuedatesSection,
  getSortedDuedates,
  UserMessageError,
  useUpdateTaskMutation,
  type Task,
  type UpdateTask
} from 'prove-common'
import { useQueryClient } from '@tanstack/react-query'
import { useTaskReceptionAddress } from '../../hooks/useTaskReceptionAddress'

type TaskDuedateTabProps = {
  task: Task
}

export const TaskDuedateTab = ({ task }: TaskDuedateTabProps) => {
  const updateTask = useUpdateTaskMutation()
  const queryClient = useQueryClient()
  const receptionAddress = useTaskReceptionAddress(
    task.lab?.id,
    task.labReceptionAddressId
  )

  const buildUpdatedTask = (duedates: string[]): UpdateTask => ({
    quantity: task.quantity ?? 0,
    countSubSamples: task.countSubSamples,
    sampntTypeId: task.sampntType.id,
    labId: task.lab?.id,
    labReceptionAddressId: task.labReceptionAddressId,
    description: task.description,
    ownerId: task.ownerId,
    analysisTypeId: task.analysisType.id,
    planStatus: task.planStatus,
    ordningsverdiId: task.ordningsVerdi.id,
    locationRequired: task.locationRequired,
    contractId: task.contractId,
    matrixAmount: task.matrixAmount ?? null,
    duedates
  })

  const saveDuedates = async (duedates: string[]) => {
    if (!receptionAddress.canSave) {
      throw new UserMessageError(receptionAddress.saveBlockedMessage)
    }
    await updateTask.mutateAsync({
      taskId: task.id,
      etag: task.etag ?? '',
      body: buildUpdatedTask(duedates)
    })

    await queryClient.invalidateQueries({ queryKey: ['tasks'] })
  }

  return (
    <DuedatesSection
      duedates={task.duedates}
      hideHeading
      isPending={updateTask.isPending || !receptionAddress.canSave}
      onAddDuedate={async (duedate) => {
        const sortedDuedates = getSortedDuedates(task.duedates)

        if (sortedDuedates.includes(duedate)) {
          throw new UserMessageError(
            'Fristen finnes allerede for denne oppgaven, og vil ikke bli lagt til.'
          )
        }

        await saveDuedates(getSortedDuedates([...sortedDuedates, duedate]))
      }}
      onRemoveDuedate={async (duedate) => {
        await saveDuedates(
          getSortedDuedates(task.duedates).filter((date) => date !== duedate)
        )
      }}
    />
  )
}
